import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trophy, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SCOPES = [
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
  { id: "alltime", label: "All-Time" },
];

export default function PowerLeaderboard() {
  const nav = useNavigate();
  const [scope, setScope] = useState("alltime");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    fetch(`${API}/api/power/leaderboard?scope=${scope}`, { credentials: "include" })
      .then((r) => r.json()).then(setData).catch(() => {});
  }, [scope]);

  return (
    <div className="min-h-screen spotlight grain scanlines">
      <div className="max-w-md mx-auto px-5 pt-6 pb-nav">
        <button data-testid="power-lb-back" onClick={() => nav("/home")}
          className="ml-12 flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FFD700]">
          <ArrowLeft size={16} /> Home
        </button>

        <div className="mt-6 flex items-center gap-2">
          <Zap size={20} className="text-[#FFD700]" />
          <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#FFD700]">Power Smash Leaderboard</div>
        </div>
        <h1 className="mt-1 font-display text-5xl leading-[0.9] tracking-tight">Top Primes</h1>

        <div className="mt-4 flex gap-1.5 flex-wrap" data-testid="power-lb-tabs">
          {SCOPES.map((s) => (
            <button key={s.id}
              data-testid={`power-lb-tab-${s.id}`}
              onClick={() => setScope(s.id)}
              className={`px-3.5 py-1.5 rounded-full text-[11px] tracking-[0.15em] uppercase font-bold transition-all ${
                scope === s.id
                  ? "bg-[#FFD700] text-[#0A090D]"
                  : "bg-black/40 border border-white/15 text-[#FDFBF7]/70 hover:border-[#FFD700]/40"
              }`}>
              {s.label}
            </button>
          ))}
        </div>

        {!data && (
          <div className="mt-8 text-center text-[#FDFBF7]/50 text-sm">Loading rankings…</div>
        )}
        {data && data.rows.length === 0 && (
          <div className="mt-8 text-center text-[#FDFBF7]/50 text-sm" data-testid="power-lb-empty">
            No games yet. Be the first to power smash.
          </div>
        )}
        {data && data.rows.length > 0 && (
          <div className="mt-5 space-y-2" data-testid="power-lb-rows">
            {data.rows.map((r, i) => (
              <motion.div
                key={r.user_id || r.rank}
                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.02 }}
                data-testid={`power-lb-row-${r.rank}`}
                className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${
                  r.rank === 1 ? "border-[#FFD700]/70 bg-[#FFD700]/10"
                  : r.rank <= 3 ? "border-[#FF6B35]/50 bg-[#FF6B35]/5"
                  : "border-white/10 bg-black/40"
                }`}
              >
                <div className={`w-8 text-center font-display text-2xl tabular ${
                  r.rank === 1 ? "text-[#FFD700]"
                  : r.rank === 2 ? "text-[#00E5FF]"
                  : r.rank === 3 ? "text-[#FF6B35]" : "text-[#FDFBF7]/60"
                }`}>{r.rank}</div>
                {r.picture ? (
                  <img src={r.picture} alt="" className="w-10 h-10 rounded-xl border border-white/20" />
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-[#FF107A]/20 border border-[#FF107A]/40 flex items-center justify-center font-bold text-[#FF107A]">
                    {r.name?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#FDFBF7] truncate">{r.name}</div>
                  <div className="text-[10px] tracking-[0.15em] uppercase text-[#FDFBF7]/60 truncate">
                    {r.title}
                    {r.games ? ` · ${r.games}g` : (r.games_played ? ` · ${r.games_played}g` : "")}
                    {typeof r.perfects === "number" ? ` · ${r.perfects} perfect` : (r.perfect_rounds ? ` · ${r.perfect_rounds} perfect` : "")}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl tabular text-[#FFD700]">
                    {scope === "alltime" ? r.power_score : r.score}
                  </div>
                  <div className="text-[9px] tracking-[0.2em] uppercase text-[#FDFBF7]/50">pts</div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <button data-testid="power-lb-play" onClick={() => nav("/power")}
          className="mt-8 w-full rounded-full py-3 font-bold text-[#0A090D] flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(90deg,#FFD700,#FF6B35)" }}>
          <Zap size={16} /> Play Power Smash
        </button>
      </div>
    </div>
  );
}
